import { IconExclamationTriangle } from '../ui/icons';
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Générer un ID unique pour l'erreur
    const errorId = Date.now().toString(36) + Math.random().toString(36).substr(2);
    
    return { 
      hasError: true, 
      error: error,
      errorInfo: null,
      errorId: errorId
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log l'erreur pour le monitoring
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // En production, on pourrait envoyer l'erreur à un service de monitoring
    if (process.env.NODE_ENV === 'production') {
      // this.logErrorToService(error, errorInfo, this.state.errorId);
    }
  }

  // Méthode pour reset l'erreur
  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-lg w-full bg-white rounded-lg shadow-lg border p-6">
            <div className="text-center">
              <div className="text-red-500 text-6xl mb-4"><IconExclamationTriangle className="inline-block h-[1em] w-[1em] align-[-0.125em]" aria-hidden="true" /></div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Oops ! Une erreur s'est produite
              </h2>
              <p className="text-gray-600 mb-6">
                Un problème technique empêche l'affichage de cette section. 
                Notre équipe a été notifiée et travaille à résoudre le problème.
              </p>
              
              {/* Message d'erreur visible aussi en ligne (replié) : sans lui, un plantage signalé
                  par un visiteur est impossible à diagnostiquer. La pile des composants reste
                  réservée au développement. */}
              <details className="text-left bg-gray-100 p-4 rounded-md mb-4">
                <summary className="cursor-pointer font-medium text-gray-700 mb-2">
                  Détails techniques
                </summary>
                <div className="text-xs text-gray-600 overflow-auto">
                  <div className="mb-2">
                    <strong>ID Erreur:</strong> {this.state.errorId}
                  </div>
                  <div className="mb-2">
                    <strong>Page:</strong> {window.location.pathname}
                  </div>
                  <div className="mb-2">
                    <strong>Erreur:</strong>
                  </div>
                  <pre className="bg-red-50 p-2 rounded mb-2 whitespace-pre-wrap break-words">
                    {this.state.error && this.state.error.toString()}
                    {/* Premières lignes de la pile : elles situent l'erreur dans le code */}
                    {this.state.error && this.state.error.stack &&
                      '\n' + String(this.state.error.stack).split('\n').slice(1, 4).join('\n')}
                  </pre>
                  {process.env.NODE_ENV === 'development' && this.state.errorInfo && this.state.errorInfo.componentStack && (
                    <>
                      <div className="mb-2">
                        <strong>Stack de composants:</strong>
                      </div>
                      <pre className="bg-yellow-50 p-2 rounded">
                        {this.state.errorInfo.componentStack}
                      </pre>
                    </>
                  )}
                </div>
              </details>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={this.resetError}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Réessayer
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors"
                >
                  Recharger la page
                </button>
                <button
                  onClick={() => window.history.back()}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Retour
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;